import { Injectable } from '@angular/core';
import {
  AuthChangeEvent,
  AuthTokenResponsePassword,
  createClient,
  Session,
  SupabaseClient,
  UserResponse,
} from '@supabase/supabase-js';
import { isUserRole, UserRole } from '@repo/models';
import { catchError, from, map, Observable, of, switchMap, take, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly client: SupabaseClient;

  constructor() {
    this.client = createClient(
      environment.databaseUrl.supabase,
      environment.databaseAnonKey.supabase
    );
  }

  public async signInWithPassword(
    email: string,
    password: string
  ): Promise<AuthTokenResponsePassword> {
    return this.client.auth.signInWithPassword({ email, password });
  }

  public async signOut(): Promise<void> {
    await this.client.auth.signOut();
  }

  public async getSession() {
    return this.client.auth.getSession();
  }

  public getRole(session: Session | null): UserRole | null {
    const role = session?.user.app_metadata?.['role'];
    return isUserRole(role) ? role : null;
  }

  public isOperator(session: Session | null): boolean {
    return this.getRole(session) === UserRole.Operator;
  }

  public async updatePassword(password: string): Promise<UserResponse> {
    return this.client.auth.updateUser({ password });
  }

  public changePassword(
    email: string,
    oldPassword: string,
    newPassword: string
  ): Observable<UserResponse> {
    return this.isAuthenticatedUser(email).pipe(
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) {
          return throwError(() => new Error('User is not authenticated'));
        }
        return this.isVerifiedUser(email, oldPassword);
      }),
      switchMap((isVerified) => {
        if (!isVerified) {
          return throwError(() => new Error('Current password verification failed'));
        }
        return from(this.client.auth.updateUser({ password: newPassword }));
      }),
      map((response) => {
        if (response.error) {
          throw response.error;
        }
        return response;
      }),
      catchError((err) => {
        console.error('Update failed:', err.message);
        return throwError(() => err);
      })
    );
  }

  public onAuthStateChange(callback: (event: AuthChangeEvent, session: Session | null) => void) {
    return this.client.auth.onAuthStateChange((event, session) => callback(event, session));
  }

  public async verifyInviteToken(token: string, type: string) {
    return this.client.auth.verifyOtp({
      token_hash: token,
      type: type as any, // 'invite', 'magiclink', etc.
    });
  }

  private isAuthenticatedUser(email: string): Observable<boolean> {
    return from(this.client.auth.getSession()).pipe(
      take(1),
      map(({ data, error }) => {
        if (error || !data.session) return false;
        return data.session.user.email === email;
      }),
      catchError((error) => {
        console.error(error);
        return of(false);
      })
    );
  }

  private isVerifiedUser(email: string, password: string): Observable<boolean> {
    return from(this.client.auth.signInWithPassword({ email, password })).pipe(
      take(1),
      map(({ data, error }) => {
        if (error || !data.session) return false;
        return data.session.user.email === email;
      }),
      catchError((error) => {
        console.error(error);
        return of(false);
      })
    );
  }
}
